
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GroupLeaderRequest } from "@/types/admin-requests";
import { logError } from "@/utils/sessionManager";

/**
 * Hook to fetch and manage request data with improved security
 */
export const useRequestData = (
  setRequests: (requests: GroupLeaderRequest[]) => void,
  setIsLoading: (isLoading: boolean) => void
) => {
  const { toast } = useToast();

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      // Verify session before fetching data
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session found");
      }
      
      const { data, error } = await supabase
        .from('group_leader_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Log the fetched requests data for debugging
      console.log('Fetched requests from database:', data);
      
      // Check what email_sent values we have
      const emailSentStats = {
        true: data?.filter(r => r.email_sent === true).length || 0,
        false: data?.filter(r => r.email_sent === false).length || 0,
        null: data?.filter(r => r.email_sent === null).length || 0,
        undefined: data?.filter(r => r.email_sent === undefined).length || 0
      };
      console.log('Email sent statistics:', emailSentStats);
      
      // Check for approved requests that need invitations
      const needsInvitation = data?.filter(r => 
        r.status === 'approved' && r.email_sent === false
      );
      console.log('Requests that need invitations:', needsInvitation?.length || 0);
      
      setRequests(data || []);
    } catch (error: any) {
      logError(error, "fetchRequests");
      console.error("Error fetching requests:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [setRequests, setIsLoading, toast]);

  return { fetchRequests };
};
